import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { nonNegativeFloatRule, requireRule } from "src/components/common/forms/validation.ts";
import { spanGridColumns } from "src/components/common/forms/styles.ts";
import {
	SalesLeadsApi,
	SalesLeadsApi_FormInitData,
} from "src/api/generated/erp/sales/crm/salesLeads/api/salesLeadsApi.ts";
import { CustomerApi } from "src/api/generated/erp/sales/customer/api/customerApi.ts";
import { SalesLead } from "src/api/generated/erp/db/types/tables/salesLead.ts";
import { Customer } from "src/api/generated/erp/db/types/tables/customer.ts";
import { CustomerAddress } from "src/api/generated/erp/db/types/tables/customerAddress.ts";
import { SalesPipelinePhase } from "src/api/generated/erp/db/types/tables/salesPipelinePhase.ts";
import { DeepPartial } from "react-hook-form";
import i18n from "i18next";
import { getCustomerTypeLabels } from "src/api/generated/erp/db/types/enums/customerType.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { Box } from "@mui/material";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink, faLinkSlash } from "@fortawesome/pro-regular-svg-icons";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	ObjectAttributesEmbeddedForm,
	ObjectAttributesEmbeddedFormValues,
} from "src/components/views/erp/objectAttributes/ObjectAttributesEmbeddedForm.tsx";
import {
	getObjectAttributesEmbeddedFormDefaultValues,
	getObjectAttributesFromEmbeddedFormValues,
} from "src/components/views/erp/objectAttributes/ObjectAttributesEmbeddedForm.utils.tsx";
import { dropProps } from "src/utils/dropProps.ts";
import { ContactCreateInput } from "src/api/generated/erp/contacts/model/contactCreateInput.ts";
import { Contact } from "src/api/generated/erp/db/types/tables/contact.ts";

export interface SalesLeadFormProps extends FormCommonProps<number> {
	initialization:
		| {
				type: "existingLead";
				salesLeadId: number;
		  }
		| {
				type: "newLead";
				customerId?: number;
				/** Places the new lead on the first phase of the selected pipeline. */
				placeOnPipeline?: boolean;
				salesPipelineId?: number;
				assignCurrentSalesperson?: boolean;
		  };
}

/** Contact fields edit the primary contact of the lead. */
interface SalesLeadFormValues extends SalesLead, ObjectAttributesEmbeddedFormValues {
	contactName: string;
	contactEmail: string;
	contactPhone: string;
}

export const SalesLeadForm = ({ initialization, onCompleted, onFormEdited }: SalesLeadFormProps) => {
	const { appUserId } = useGlobalInitData();
	const [lastCreatedSalesPipelineId, setLastCreatedSalesPipelineId] = useStoredState<number | null>({
		key: "8E2D5B9A417C3F60",
		type: "number",
		defaultValue: null,
		separateByEnvironmentType: true,
	});
	const salesLeadId = initialization.type === "existingLead" ? initialization.salesLeadId : undefined;
	return (
		<AsyncForm<SalesLeadsApi_FormInitData, SalesLeadFormValues, number>
			fetch={() =>
				SalesLeadsApi.getFormInitData({
					salesLeadId,
					customerId: initialization.type === "newLead" ? initialization.customerId : undefined,
				})
			}
			getDefaultValues={createSalesLeadDefaultValues}
			submit={submit}
			columns={2}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.crm.salesLead.form"),
				title: i18n.t("sales_lead"),
				data: {
					salesLeadId: salesLeadId ?? null,
					customerId: initialization.type === "newLead" ? initialization.customerId ?? null : null,
					placeOnPipeline: initialization.type === "newLead" ? initialization.placeOnPipeline ?? null : null,
					salesPipelineId:
						initialization.type === "newLead" ? (initialization.salesPipelineId ?? null) : null,
					assignCurrentSalesperson:
						initialization.type === "newLead" ? initialization.assignCurrentSalesperson ?? null : null,
				},
			}}
			render={(contentParams) => (
				<FormContent
					{...contentParams}
					canChangeCustomerLink={initialization.type === "newLead" && initialization.customerId == null}
				/>
			)}
		/>
	);

	async function submit(
		values: SalesLeadFormValues,
		{ data }: AsyncFormContentParams<SalesLeadsApi_FormInitData, SalesLeadFormValues>,
	): Promise<number> {
		const salesLead = dropProps(values, ["attributes", "contactName", "contactEmail", "contactPhone"]);
		const attributes = getObjectAttributesFromEmbeddedFormValues(values);
		if (salesLead.salesLeadId == null) {
			const placeOnPipeline = initialization.type === "newLead" && initialization.placeOnPipeline === true;
			const salesPipelinePhaseId =
				placeOnPipeline ?
					getFirstPhaseId(salesLead.salesPipelineId, data.salesPipelinePhaseOptions)
				:	undefined;
			const createdSalesLeadId = await SalesLeadsApi.insert({
				salesLead: { ...salesLead, salesPipelinePhaseId },
				attributes,
				contacts: getPrimaryContactInputs(values),
			});
			setLastCreatedSalesPipelineId(salesLead.salesPipelineId);
			return createdSalesLeadId;
		} else {
			await SalesLeadsApi.update({
				salesLead: {
					...salesLead,
					salesLeadId: salesLead.salesLeadId,
					...getChangedPrimaryContactFields(values, data.primaryContact),
				},
				attributes,
				// Lets the backend reject contact edits if the primary contact has changed meanwhile.
				expectedPrimaryContact: { contactId: data.primaryContact?.contactId ?? null },
			});
			return salesLead.salesLeadId;
		}
	}

	function createSalesLeadDefaultValues({
		salesLead,
		primaryContact,
		customer,
		defaultDocumentAddress,
		salespersonOptions,
		salesPipelineOptions,
		attributeFields,
		attributeValues,
	}: SalesLeadsApi_FormInitData): DeepPartial<SalesLeadFormValues> {
		const attributeFormValues = getObjectAttributesEmbeddedFormDefaultValues(attributeValues, attributeFields);
		if (salesLead != null)
			return {
				...salesLead,
				...attributeFormValues,
				contactName: primaryContact?.name ?? "",
				contactEmail: primaryContact?.email ?? "",
				contactPhone: primaryContact?.phone ?? "",
			};

		return {
			...DEFAULT_VALUES,
			...attributeFormValues,
			...getCustomerSalesLeadDefaults(customer, defaultDocumentAddress),
			salespersonId:
				initialization.type === "newLead" && initialization.assignCurrentSalesperson === true ?
					salespersonOptions.find((sp) => sp.appUserId === appUserId)?.salespersonId
				:	undefined,
			salesPipelineId:
				(initialization.type === "newLead" ? initialization.salesPipelineId : undefined) ??
				salesPipelineOptions.find((o) => o.salesPipelineId === lastCreatedSalesPipelineId)?.salesPipelineId ??
				salesPipelineOptions[0]?.salesPipelineId,
		};
	}
};

function getPrimaryContactInputs({
	contactName,
	contactEmail,
	contactPhone,
}: Pick<SalesLeadFormValues, "contactName" | "contactEmail" | "contactPhone">): ContactCreateInput[] {
	if (contactName.trim() === "" && contactEmail.trim() === "" && contactPhone.trim() === "") return [];
	return [
		{
			name: contactName,
			email: contactEmail,
			phone: contactPhone,
			designation: "",
			note: "",
			isContractSigner: false,
			isDefaultContact: true,
		},
	];
}

/**
 * Sends only the edited contact fields, so saving the lead does not overwrite primary contact changes made
 * elsewhere while the form was open.
 */
function getChangedPrimaryContactFields(
	{
		contactName,
		contactEmail,
		contactPhone,
	}: Pick<SalesLeadFormValues, "contactName" | "contactEmail" | "contactPhone">,
	primaryContact: Contact | null | undefined,
): Partial<Pick<SalesLeadFormValues, "contactName" | "contactEmail" | "contactPhone">> {
	return {
		...(contactName !== (primaryContact?.name ?? "") && { contactName }),
		...(contactEmail !== (primaryContact?.email ?? "") && { contactEmail }),
		...(contactPhone !== (primaryContact?.phone ?? "") && { contactPhone }),
	};
}

/** Returns undefined when the pipeline has no phases, so the lead is created outside the pipeline phases. */
function getFirstPhaseId(
	salesPipelineId: number | null | undefined,
	salesPipelinePhaseOptions: SalesPipelinePhase[],
): number | undefined {
	return salesPipelinePhaseOptions
		.filter((phase) => phase.salesPipelineId === salesPipelineId)
		.sort((a, b) => a.position - b.position)[0]?.salesPipelinePhaseId;
}

function getCustomerSalesLeadDefaults(
	customer: Customer | null | undefined,
	defaultDocumentAddress: CustomerAddress | null | undefined,
): DeepPartial<SalesLeadFormValues> {
	if (customer == null) return {};

	return {
		customerId: customer.customerId,
		customerName: customer.customerName,
		customerType: customer.customerType,
		customerGroupId: customer.customerGroupId,
		contactName: defaultDocumentAddress?.contact ?? "",
		contactEmail: defaultDocumentAddress?.email ?? "",
		contactPhone: defaultDocumentAddress?.phone ?? "",
		address_1: defaultDocumentAddress?.address_1 ?? "",
		address_2: defaultDocumentAddress?.address_2 ?? "",
		postalCode: defaultDocumentAddress?.postalCode ?? "",
		city: defaultDocumentAddress?.city ?? "",
		country: defaultDocumentAddress?.country ?? DEFAULT_VALUES.country,
	};
}

const DEFAULT_VALUES: DeepPartial<SalesLeadFormValues> = {
	customerType: "COMPANY",
	country: "FINLAND",
	customerName: "",
	contactName: "",
	contactEmail: "",
	contactPhone: "",
	address_1: "",
	address_2: "",
	postalCode: "",
	city: "",
	description: "",
};

interface FormContentProps extends AsyncFormContentParams<SalesLeadsApi_FormInitData, SalesLeadFormValues> {
	canChangeCustomerLink: boolean;
}

interface CustomerSelectionFormValues {
	customer: Customer | null;
}

const FormContent = ({ control, data, setValue, watch, canChangeCustomerLink }: FormContentProps) => {
	const showFormInput = useFormInput();
	const hasSelectedCustomer = watch("customerId") != null;

	return (
		<>
			<FormTextField
				control={control}
				name={"customerName"}
				label={i18n.t("customer_name")}
				rules={requireRule()}
				disabled={hasSelectedCustomer}
				autoFocus
			/>
			<Box
				sx={{
					display: "flex",
					gap: 1,
					alignItems: "flex-start",
					alignSelf: "flex-start",
					minWidth: 0,
				}}
			>
				{hasSelectedCustomer ?
					canChangeCustomerLink && (
						<AsyncButton
							label={i18n.t("unlink")}
							icon={faLinkSlash}
							variant={"outlined"}
							color={"secondary"}
							onClick={unlinkCustomer}
							sx={{ flex: "0 0 auto" }}
						/>
					)
				:	canChangeCustomerLink && (
						<AsyncButton
							label={i18n.t("select_customer")}
							icon={faLink}
							variant={"outlined"}
							onClick={selectExistingCustomer}
							sx={{ flex: "0 0 auto" }}
						/>
					)
				}
			</Box>
			<FormEnumSelectField
				control={control}
				name={"customerType"}
				label={i18n.t("customer_type")}
				options={getCustomerTypeLabels()}
				disableClearable
			/>
			<FormSelectField
				control={control}
				name={"customerGroupId"}
				label={i18n.t("customer_group")}
				options={data.customerGroupOptions}
				getOptionKey={(o) => o.customerGroupId}
				getOptionLabel={(o) => o.name ?? ""}
			/>
			<FormTextField control={control} name={"contactName"} label={i18n.t("contact_person")} />
			<FormTextField control={control} name={"contactEmail"} label={i18n.t("email")} />
			<FormTextField control={control} name={"contactPhone"} label={i18n.t("phone")} />
			<FormSelectField
				control={control}
				name={"salespersonId"}
				label={i18n.t("salesperson")}
				options={data.salespersonOptions}
				getOptionKey={(o) => o.salespersonId}
				getOptionLabel={(o) => o.name}
			/>
			<FormTextField
				control={control}
				name={"description"}
				label={i18n.t("description")}
				multiline
				sx={spanGridColumns}
			/>
			<FormSelectField
				control={control}
				name={"salesLeadSourceId"}
				label={i18n.t("lead_source")}
				options={data.salesLeadSourceOptions}
				getOptionKey={(o) => o.salesLeadSourceId}
				getOptionLabel={(o) => o.name}
			/>
			<FormSelectField
				control={control}
				name={"salesAreaId"}
				label={i18n.t("sales_area")}
				options={data.salesAreaOptions}
				getOptionKey={(o) => o.salesAreaId}
				getOptionLabel={(o) => o.name}
			/>
			<FormSelectField
				control={control}
				name={"salesPipelineId"}
				label={i18n.t("sales_pipeline")}
				options={data.salesPipelineOptions}
				getOptionKey={(o) => o.salesPipelineId}
				getOptionLabel={(o) => o.name}
				disableClearable
				rules={requireRule()}
			/>
			<FormTextField control={control} name={"address_1"} label={i18n.t("address_1")} />
			<FormTextField control={control} name={"address_2"} label={i18n.t("address_2")} />
			<FormTextField control={control} name={"postalCode"} label={i18n.t("postal_code")} />
			<FormTextField control={control} name={"city"} label={i18n.t("city")} />
			<FormCountrySelectField control={control} name={"country"} label={i18n.t("country")} />
			<FormNumberField
				control={control}
				name={"estimatedValue"}
				label={i18n.t("estimated_value")}
				rules={nonNegativeFloatRule()}
			/>
			<ObjectAttributesEmbeddedForm control={control} />
		</>
	);

	async function selectExistingCustomer() {
		const result = await showFormInput<CustomerSelectionFormValues>({
			title: i18n.t("select_customer"),
			size: "sm",
			submitLabel: i18n.t("ok"),
			defaultValues: {
				customer: null,
			},
			content: ({ control }) => (
				<FormAsyncSelectField<CustomerSelectionFormValues, Customer, number, "customer">
					control={control}
					name={"customer"}
					label={i18n.t("customer")}
					placeholder={i18n.t("search_existing_customer")}
					getOptionKey={(customer) => customer.customerId}
					getOptionLabel={(customer) => customer.customerName}
					fetchOptions={({ searchQuery, currentSelection }) =>
						CustomerApi.getCustomerSelectionOptions({
							currentSelection: currentSelection,
							searchQuery: searchQuery,
						})
					}
					formValueType={"option"}
					rules={requireRule()}
					autoFocus
				/>
			),
		});

		if (result?.customer == null) return;
		await applySelectedCustomer(result.customer);
	}

	async function applySelectedCustomer(customer: Customer) {
		const customerAddresses = await CustomerApi.getCustomerAddresses({
			customerId: customer.customerId,
			addressType: "DOCUMENT",
		});
		const defaultDocumentAddress = customerAddresses.find((address) => address.isDefault);
		const customerDefaults = getCustomerSalesLeadDefaults(customer, defaultDocumentAddress);

		setValue("customerId", customerDefaults.customerId, CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("customerName", customerDefaults.customerName ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("customerType", customerDefaults.customerType ?? "COMPANY", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("customerGroupId", customerDefaults.customerGroupId, CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("contactName", customerDefaults.contactName ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("contactEmail", customerDefaults.contactEmail ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("contactPhone", customerDefaults.contactPhone ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("address_1", customerDefaults.address_1 ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("address_2", customerDefaults.address_2 ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("postalCode", customerDefaults.postalCode ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("city", customerDefaults.city ?? "", CUSTOMER_LINK_VALUE_OPTIONS);
		setValue("country", customerDefaults.country ?? DEFAULT_VALUES.country, CUSTOMER_LINK_VALUE_OPTIONS);
	}

	function unlinkCustomer() {
		setValue("customerId", null, CUSTOMER_LINK_VALUE_OPTIONS);
	}
};

const CUSTOMER_LINK_VALUE_OPTIONS = {
	shouldDirty: true,
	shouldValidate: true,
};
